import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactQuill, { Quill } from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import "../../styles/noticeEditor.css";
import { toast } from "react-toastify";
import { t } from "i18next";
import ImageResize from "../../utils/quillImageResize";
import { uploadNoticeImage } from "../../services/noticeService";
import { resolveServerUrl } from "../../utils/urlUtils";

// 이미지 드래그 리사이즈 모듈 등록 (모듈 로드 시 1회)
Quill.register("modules/imageResize", ImageResize);

const TOOLBAR = [
  [{ header: [1, 2, 3, false] }],
  ["bold", "italic", "underline", "strike"],
  [{ list: "ordered" }, { list: "bullet" }],
  ["blockquote", "code-block"],
  ["link", "image"],
  ["clean"],
];

const FORMATS = ["header", "bold", "italic", "underline", "strike", "list", "blockquote", "code-block", "link", "image"];

const hasImageFile = (fileList?: FileList | DataTransferItemList | null) =>
  !!fileList && fileList.length > 0 && Array.from(fileList as any).some((f: any) => f.type?.startsWith("image/"));

export default function NoticeEditor({
  value,
  onChange,
  placeholder,
  onImageUploaded,
}: {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  onImageUploaded?: (url: string) => void;
}) {
  const quillRef = useRef<ReactQuill>(null);
  // 콜백을 ref 로 보관해 핸들러/modules 정체성을 안정적으로 유지
  const onImageUploadedRef = useRef(onImageUploaded);
  onImageUploadedRef.current = onImageUploaded;
  // 동시 업로드 추적용 카운터 (0 보다 크면 업로드 중)
  const [uploadCount, setUploadCount] = useState(0);
  const uploading = uploadCount > 0;

  // 파일 1개를 업로드한 뒤 지정 위치에 삽입하고, 다음 삽입 위치를 반환
  const uploadAndInsertAt = useCallback(async (file: File, index: number) => {
    const editor = quillRef.current?.getEditor();
    if (!editor) return index;
    try {
      setUploadCount((c) => c + 1);
      const url = await uploadNoticeImage(file);
      // 업로드된 이미지를 세션 추적 목록에 보고(취소/저장 시 정리에 사용)
      onImageUploadedRef.current?.(url);
      // 저장은 상대경로로, 미리보기는 서버 절대 URL 로
      editor.insertEmbed(index, "image", resolveServerUrl(url));
      editor.setSelection(index + 1, 0);
      return index + 1;
    } catch (error: any) {
      toast.error(error?.response?.data?.message || t("ImageUploadFailedMsg"));
      return index;
    } finally {
      setUploadCount((c) => c - 1);
    }
  }, []);

  // 여러 이미지 파일을 startIndex 부터 순서대로 업로드/삽입
  const handleFiles = useCallback(
    async (fileList: FileList, startIndex?: number) => {
      const editor = quillRef.current?.getEditor();
      if (!editor) return;
      const images = Array.from(fileList).filter((f) => f.type.startsWith("image/"));
      if (images.length === 0) return;
      let index = startIndex ?? editor.getSelection()?.index ?? editor.getLength();
      for (const file of images) {
        index = await uploadAndInsertAt(file, index);
      }
    },
    [uploadAndInsertAt],
  );

  // 툴바 이미지 버튼: 파일 선택 → 업로드
  const imageHandler = useCallback(() => {
    const input = document.createElement("input");
    input.setAttribute("type", "file");
    input.setAttribute("accept", "image/*");
    input.setAttribute("multiple", "true");
    input.click();
    input.onchange = () => {
      if (!input.files?.length) return;
      const editor = quillRef.current?.getEditor();
      const range = editor?.getSelection(true);
      handleFiles(input.files, range ? range.index : undefined);
    };
  }, [handleFiles]);

  // 붙여넣기 / 드래그앤드롭 으로도 동일한 업로드 흐름을 타게 한다.
  // 캡처 단계에서 가로채 Quill 의 기본 동작(base64 삽입 등)을 막는다.
  useEffect(() => {
    const editor = quillRef.current?.getEditor();
    if (!editor) return;
    const root = editor.root;

    const onPaste = (e: ClipboardEvent) => {
      if (!hasImageFile(e.clipboardData?.files)) return; // 텍스트 붙여넣기는 그대로 둠
      e.preventDefault();
      e.stopPropagation();
      const range = editor.getSelection();
      handleFiles(e.clipboardData!.files, range ? range.index : undefined);
    };

    const onDrop = (e: DragEvent) => {
      if (!hasImageFile(e.dataTransfer?.files)) return;
      e.preventDefault();
      e.stopPropagation();
      const range = editor.getSelection();
      handleFiles(e.dataTransfer!.files, range ? range.index : undefined);
    };

    // 드롭을 받으려면 dragover 의 기본 동작을 막아야 한다.
    const onDragOver = (e: DragEvent) => {
      if (hasImageFile(e.dataTransfer?.items) || e.dataTransfer?.types?.includes("Files")) {
        e.preventDefault();
      }
    };

    root.addEventListener("paste", onPaste, true);
    root.addEventListener("drop", onDrop, true);
    root.addEventListener("dragover", onDragOver, true);
    return () => {
      root.removeEventListener("paste", onPaste, true);
      root.removeEventListener("drop", onDrop, true);
      root.removeEventListener("dragover", onDragOver, true);
    };
  }, [handleFiles]);

  // modules 는 재생성되면 에디터가 재초기화되므로 정체성을 고정한다
  const modules = useMemo(
    () => ({
      toolbar: {
        container: TOOLBAR,
        handlers: { image: imageHandler },
      },
      // 이미지 클릭 시 4코너 핸들로 크기 조절 (Shift = 비율 고정)
      imageResize: {},
    }),
    [imageHandler],
  );

  return (
    <div className="notice-editor">
      <ReactQuill ref={quillRef} theme="snow" value={value || ""} onChange={onChange} modules={modules} formats={FORMATS} placeholder={placeholder} />
      {uploading && <div className="notice-editor__uploading">{t("UploadingImageMsg")}</div>}
    </div>
  );
}
